import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { DASHBOARD } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MapPin, FileText, ClipboardList, CheckCircle2, AlertTriangle, FolderCheck,
  TrendingDown, History, ArrowRight, Leaf, Lock, ShieldAlert, CalendarClock, Target, ListChecks,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
  LineChart, Line, PieChart, Pie, CartesianGrid,
} from "recharts";
import { prettyStatus, STATUS_STYLES } from "@/lib/format";
import { cn } from "@/lib/utils";

const STAT_META = [
  { key: "sites", label: "Sites", icon: MapPin, testid: DASHBOARD.statSites, tint: "text-forest-mid" },
  { key: "contracts", label: "Contracts", icon: FileText, testid: DASHBOARD.statContracts, tint: "text-sage-accent" },
  { key: "open_work_orders", label: "Open work orders", icon: ClipboardList, testid: DASHBOARD.statOpenWo, tint: "text-blue-600" },
  { key: "needs_verification", label: "Awaiting verify", icon: CheckCircle2, testid: DASHBOARD.statVerify, tint: "text-amber-600" },
  { key: "indicators_off_track", label: "Indicators off-track", icon: TrendingDown, testid: DASHBOARD.statOffTrack, tint: "text-rose-600" },
  { key: "evidence_packs", label: "Evidence packs", icon: FolderCheck, testid: DASHBOARD.statPacks, tint: "text-forest-dark" },
];

const BAR_COLORS = {
  draft: "#94a3b8", triaged: "#2563eb", approved: "#0d9488", scheduled: "#4f46e5",
  in_progress: "#d97706", completed: "#059669", verified: "#27523C", closed: "#1e293b",
};

const BAND_HEX = {
  advanced: "#1f5138", strong: "#2f6b4f", developing: "#b07a16",
  early_progress: "#c2410c", priority_improvement: "#b91c1c", restricted: "#b91c1c",
};
const PILLAR_HEX = { environmental: "#27523C", social: "#6aa088", economic: "#b07a16" };

function ActionCard({ testid, icon: Icon, tint, count, label, hint, onClick }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="group rounded-xl border border-sage-light bg-white p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-forest-mid hover:shadow-md">
      <Icon className={cn("h-5 w-5", tint)} />
      <p className="mt-2 font-heading text-3xl font-bold text-forest-dark">{count}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {hint ? <p className="mt-1 truncate text-[11px] font-medium text-forest-mid">{hint}</p> : null}
    </button>
  );
}

function Gaugey({ pct }) {
  const p = Math.max(0, Math.min(100, Math.round(pct || 0)));
  const color = p >= 75 ? "#27523C" : p >= 50 ? "#b07a16" : "#c2410c";
  return (
    <div className="mt-3 flex items-center justify-center">
      <div className="relative h-28 w-28 rounded-full" style={{ background: `conic-gradient(${color} ${p * 3.6}deg, #eef1ec 0deg)` }}>
        <div className="absolute inset-[11px] flex items-center justify-center rounded-full bg-white">
          <span className="font-heading text-2xl font-bold text-forest-dark">{p}%</span>
        </div>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <p className="py-10 text-center text-sm text-muted-foreground">{text}</p>;
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [cockpit, setCockpit] = useState(null);
  const { user, roleLabel } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/dashboard/cockpit").then((r) => setCockpit(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Loading dashboard…</div>;

  const woChart = Object.entries(data.work_order_status || {}).map(([k, v]) => ({
    name: prettyStatus(k), key: k, value: v,
  }));

  const ins = cockpit?.insights || {};
  const acts = cockpit?.actions;
  const trend = ins.trend || [];
  const prov = ins.provenance_completeness || { pct: 0, backed: 0, total: 0 };
  const pmix = ins.pillar_mix;
  const plabels = ins.pillar_labels || {};
  const bands = ins.band_distribution || [];
  const eq = ins.evidence_quality;
  const pieData = pmix
    ? ["environmental", "social", "economic"].filter((k) => pmix[k] != null).map((k) => ({ name: plabels[k] || k, key: k, value: pmix[k] }))
    : [];

  return (
    <div className="space-y-6" data-testid={DASHBOARD.root}>
      {/* hero */}
      <div
        className="relative overflow-hidden rounded-2xl p-6 text-emerald-50 sm:p-8"
        style={{
          backgroundImage:
            "linear-gradient(110deg, rgba(22,41,32,0.94), rgba(39,82,60,0.78)), url('https://images.unsplash.com/photo-1508964942454-1a56651d54ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-200/70">
          {roleLabel}
        </p>
        <h1 className="mt-1 font-heading text-2xl font-bold sm:text-3xl">Good day, {user?.name?.split(" ")[0]}</h1>
        <p className="mt-2 max-w-xl text-sm text-emerald-100/85">
          Here's your portfolio at a glance. Capture work in the field, watch performance, and assemble this month's
          traceable evidence pack.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" className="bg-sage-accent hover:bg-sage-accent/90" onClick={() => navigate("/project")}>
            Open project record <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
          <Button size="sm" variant="secondary" className="bg-white/10 text-white hover:bg-white/20" onClick={() => navigate("/field")}>
            Field mode
          </Button>
        </div>
      </div>

      {/* decision cockpit */}
      {cockpit && (
        <div className="space-y-4 duration-500 animate-in fade-in-50" data-testid={DASHBOARD.cockpit}>
          {acts && (
            <div>
              <h2 className="mb-2 font-heading text-lg font-semibold text-forest-dark">Needs your attention</h2>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
                <ActionCard testid={DASHBOARD.actKpis} icon={ListChecks} tint="text-amber-600" count={acts.kpis_awaiting.count} label="KPIs awaiting action" onClick={() => navigate("/project?tab=score")} />
                <ActionCard testid={DASHBOARD.actReady} icon={CheckCircle2} tint="text-emerald-600" count={acts.scores_ready.count} label="Scores ready to issue" onClick={() => navigate("/project?tab=score")} />
                <ActionCard testid={DASHBOARD.actBlocked} icon={Lock} tint="text-rose-600" count={acts.scores_blocked.count} label="Scores blocked" hint={acts.scores_blocked.reasons?.[0]?.[0]} onClick={() => navigate("/project?tab=score")} />
                <ActionCard testid={DASHBOARD.actCritical} icon={ShieldAlert} tint="text-rose-600" count={acts.open_critical.count} label="Open critical issues" onClick={() => navigate("/project?tab=score")} />
                <ActionCard testid={DASHBOARD.actEnding} icon={CalendarClock} tint="text-blue-600" count={acts.contracts_ending.count} label="Contracts ending ≤90d" hint={acts.contracts_ending.items?.[0]?.name} onClick={() => navigate("/setup?tab=contracts")} />
                <ActionCard testid={DASHBOARD.actTenderGaps} icon={Target} tint="text-forest-mid" count={acts.tender_gaps.count} label="Open tender gaps" hint={acts.tender_gaps.pursuits ? `${acts.tender_gaps.pursuits} pursuit${acts.tender_gaps.pursuits === 1 ? "" : "s"}` : null} onClick={() => navigate("/setup/wizard")} />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="border-sage-light p-5 lg:col-span-2" data-testid={DASHBOARD.insTrend}>
              <h3 className="mb-3 font-heading text-base font-semibold text-forest-dark">Portfolio score trend</h3>
              {trend.length === 0 ? <Empty text="No issued assessments yet." /> : (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={trend} margin={{ top: 4, right: 12, left: -18, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1ec" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="avg" stroke="#27523C" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>
            <Card className="border-sage-light p-5" data-testid={DASHBOARD.insProvenance}>
              <h3 className="font-heading text-base font-semibold text-forest-dark">Data provenance completeness</h3>
              <p className="text-xs text-muted-foreground">Scored KPIs backed by linked source records</p>
              <Gaugey pct={prov.pct} />
              <p className="mt-1 text-center text-xs text-muted-foreground">{prov.backed} of {prov.total} scored KPIs linked</p>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="border-sage-light p-5" data-testid={DASHBOARD.insPillarMix}>
              <h3 className="mb-1 font-heading text-base font-semibold text-forest-dark">Pillar mix</h3>
              {pieData.length === 0 ? <Empty text="No issued assessments yet." /> : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                      {pieData.map((d) => <Cell key={d.key} fill={PILLAR_HEX[d.key] || "#27523C"} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
              {pieData.length > 0 && (
                <div className="mt-1 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[11px]">
                  {pieData.map((d) => (
                    <span key={d.key} className="inline-flex items-center gap-1 text-muted-foreground">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: PILLAR_HEX[d.key] }} /> {d.name} {d.value}
                    </span>
                  ))}
                </div>
              )}
            </Card>

            <Card className="border-sage-light p-5" data-testid={DASHBOARD.insBands}>
              <h3 className="mb-3 font-heading text-base font-semibold text-forest-dark">Score band distribution</h3>
              {bands.length === 0 ? <Empty text="No issued assessments yet." /> : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={bands} margin={{ top: 4, right: 8, left: -20, bottom: 4 }}>
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-16} textAnchor="end" height={48} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip cursor={{ fill: "rgba(39,82,60,0.06)" }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {bands.map((d, i) => <Cell key={i} fill={BAND_HEX[d.key] || "#2f6b4f"} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card className="border-sage-light p-5" data-testid={DASHBOARD.insEvidence}>
              <h3 className="font-heading text-base font-semibold text-forest-dark">Evidence quality index</h3>
              <p className="text-xs text-muted-foreground">Average across latest issued assessments</p>
              {eq == null ? <Empty text="No issued assessments yet." /> : (
                <div className="mt-6">
                  <p className="font-heading text-5xl font-bold text-forest-dark">{eq}<span className="text-base font-normal text-muted-foreground"> / 100</span></p>
                  <div className="mt-4 h-2.5 rounded-full bg-slate-100">
                    <div className="h-2.5 rounded-full bg-forest-mid transition-all" style={{ width: `${Math.min(100, eq)}%` }} />
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* stat tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {STAT_META.map((s) => (
          <Card key={s.key} data-testid={s.testid} className="border-sage-light p-4 transition-shadow hover:shadow-md">
            <s.icon className={cn("h-5 w-5", s.tint)} />
            <p className="mt-3 font-heading text-3xl font-bold text-forest-dark">{data.counts[s.key]}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* work order status chart */}
        <Card className="border-sage-light p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold text-forest-dark">Work order pipeline</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate("/project")}>View all</Button>
          </div>
          {woChart.length === 0 ? (
            <p className="text-sm text-muted-foreground">No work orders yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={woChart} margin={{ top: 4, right: 8, left: -18, bottom: 4 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-18} textAnchor="end" height={54} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "rgba(39,82,60,0.06)" }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {woChart.map((d) => <Cell key={d.key} fill={BAR_COLORS[d.key] || "#27523C"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* off track */}
        <Card className="border-sage-light p-5">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest-dark">
            <AlertTriangle className="h-5 w-5 text-amber-600" /> Indicators drifting
          </h3>
          {data.off_track.length === 0 ? (
            <div className="flex items-center gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
              <Leaf className="h-4 w-4" /> All indicators on target.
            </div>
          ) : (
            <div className="space-y-2">
              {data.off_track.map((o, i) => (
                <div key={i} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                  <p className="text-sm font-semibold text-forest-dark">{o.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Now <span className="font-mono font-semibold text-amber-700">{o.latest}{o.unit}</span> · target {o.target}{o.unit}
                  </p>
                </div>
              ))}
              <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/project")}>
                Assign corrective actions
              </Button>
            </div>
          )}
        </Card>
      </div>

      {/* recent activity + sites */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-sage-light p-5 lg:col-span-2">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest-dark">
            <History className="h-5 w-5 text-forest-mid" /> Recent activity
          </h3>
          <div className="divide-y">
            {data.recent_activity.length === 0 && <p className="text-sm text-muted-foreground">No activity recorded.</p>}
            {data.recent_activity.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-2.5 text-sm">
                <div>
                  <span className="font-medium text-forest-dark capitalize">{a.entity_type.replace(/_/g, " ")}</span>{" "}
                  <span className="text-muted-foreground">{a.action.replace(/_/g, " ")}</span>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <div>{a.user_name}</div>
                  <div>{fmtDateTime(a.timestamp)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="border-sage-light p-5">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-forest-dark">
            <MapPin className="h-5 w-5 text-forest-mid" /> Sites
          </h3>
          <div className="space-y-2">
            {data.sites.map((s) => (
              <div key={s.id} className="rounded-lg border border-sage-light bg-sage-surface p-3">
                <p className="text-sm font-semibold text-forest-dark">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.location} · {s.area_sqm?.toLocaleString()} m²</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
